/**
 * WhatsAppUpdateModal — agent sends a WhatsApp text update to a property's
 * buyer, from either the Offers Received tab or the Milestones dashboard.
 * Uses the platform's own WhatsApp number (POST /property-admin/whatsapp/send-update).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getApiUrl } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, MessageCircle } from "lucide-react";

const API = getApiUrl();

export default function WhatsAppUpdateModal({
  open, onOpenChange, propertyId, recipientNumber, recipientName, defaultMessage = "",
}) {
  const [message, setMessage] = useState(defaultMessage);
  const [sending, setSending] = useState(false);

  useEffect(() => { if (open) setMessage(defaultMessage); }, [open, defaultMessage]);

  const handleSend = async () => {
    if (!message.trim()) {
      toast.error("Enter a message to send");
      return;
    }
    setSending(true);
    try {
      await axios.post(
        `${API}/property-admin/whatsapp/send-update`,
        { property_id: propertyId, message: message.trim(), recipient_number: recipientNumber || undefined },
        { withCredentials: true }
      );
      toast.success(`WhatsApp message sent to ${recipientName || "the buyer"}`);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send WhatsApp message");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-none" data-testid="whatsapp-update-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4 text-emerald-600" /> Message {recipientName || "buyer"} on WhatsApp
          </DialogTitle>
          <DialogDescription>
            {recipientNumber ? `Sending to ${recipientNumber}.` : ""} Only delivers if they've messaged your WhatsApp number before.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label htmlFor="wa-update-message" className="text-xs">Message</Label>
          <Textarea
            id="wa-update-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="rounded-none mt-1 min-h-[120px]"
            data-testid="whatsapp-update-message-input"
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={() => onOpenChange(false)} disabled={sending}>
            Cancel
          </Button>
          <Button
            className="rounded-none"
            onClick={handleSend}
            disabled={sending}
            data-testid="whatsapp-update-send-btn"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <MessageCircle className="h-4 w-4 mr-2" />}
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
